import { useState, useEffect, useCallback } from 'react';
import Breadcrumb from '@/components/common/Breadcrumb';
import { listUploads, UploadRecord } from '@/utils/uploadApi';

const UPLOAD_TYPES = [
  { value: 'Book', label: '書籍' },
  { value: 'Product', label: '商品' },
  { value: 'Menu', label: '検索メニュー' },
  { value: 'Corner', label: 'コーナー' },
  { value: 'Display', label: 'ディスプレイ' },
  { value: 'Special', label: 'スペシャル' },
  { value: 'Information', label: 'インフォメーション' },
  { value: 'Coupon', label: 'クーポン' },
];

const STATUS_LABELS: Record<string, { text: string; className: string }> = {
  PENDING: { text: '待機中', className: 'bg-yellow-100 text-yellow-800' },
  PROCESSING: { text: '処理中', className: 'bg-blue-100 text-blue-800' },
  SUCCESS: { text: '成功', className: 'bg-green-100 text-green-800' },
  PARTIAL_SUCCESS: { text: '一部成功', className: 'bg-orange-100 text-orange-800' },
  FAILED: { text: '失敗', className: 'bg-red-100 text-red-800' },
};

const UploadList = () => {
  const [records, setRecords] = useState<UploadRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<UploadRecord | null>(null);

  // Search form state
  const [uploadType, setUploadType] = useState('Book');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Pagination
  const [nextToken, setNextToken] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const fetchUploads = useCallback(
    async (token?: string) => {
      setLoading(true);
      setError(null);
      try {
        const result = await listUploads({
          upload_type: uploadType,
          from_date: fromDate ? new Date(fromDate).toISOString() : undefined,
          to_date: toDate ? new Date(toDate + 'T23:59:59').toISOString() : undefined,
          limit: 50,
          next_token: token || undefined,
        });
        if (token) {
          setRecords((prev) => [...prev, ...result.items]);
        } else {
          setRecords(result.items);
        }
        setNextToken(result.next_token);
      } catch (err) {
        console.error('Failed to fetch uploads:', err);
        setError('データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    },
    [uploadType, fromDate, toDate]
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setHasSearched(true);
    fetchUploads();
  };

  const handleLoadMore = () => {
    if (nextToken) {
      fetchUploads(nextToken);
    }
  };

  // Initial load
  useEffect(() => {
    setHasSearched(true);
    fetchUploads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div>
      <Breadcrumb />
      <div className="card max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-admin-text mb-6">アップロード一覧</h1>

        {/* Search Form */}
        <form onSubmit={handleSearch} className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-admin-text mb-1">
                アップロード種別
              </label>
              <select
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {UPLOAD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-admin-text mb-1">開始日</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-admin-text mb-1">終了日</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <button
                type="submit"
                disabled={loading}
                className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
              >
                {loading ? '検索中...' : '検索'}
              </button>
            </div>
          </div>
        </form>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-4 mb-4">
            {error}
          </div>
        )}

        {/* Table */}
        {hasSearched && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-100 text-admin-text">
                <tr>
                  <th className="px-4 py-3 font-medium">アップロード種別</th>
                  <th className="px-4 py-3 font-medium">ファイル名</th>
                  <th className="px-4 py-3 font-medium">ステータス</th>
                  <th className="px-4 py-3 font-medium">担当者</th>
                  <th className="px-4 py-3 font-medium">日時</th>
                  <th className="px-4 py-3 font-medium text-center">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {records.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-admin-textLight">
                      データがありません
                    </td>
                  </tr>
                ) : (
                  records.map((record) => {
                    const status = STATUS_LABELS[record.status] || {
                      text: record.status,
                      className: 'bg-gray-100 text-gray-800',
                    };
                    return (
                      <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3">{record.upload_type}</td>
                        <td className="px-4 py-3 font-mono text-xs">{record.file_name}</td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${status.className}`}
                          >
                            {status.text}
                          </span>
                        </td>
                        <td className="px-4 py-3">{record.upload_user}</td>
                        <td className="px-4 py-3 text-xs text-admin-textLight">
                          {formatDate(record.created_at)}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setSelectedRecord(record)}
                            className="px-3 py-1 text-xs bg-gray-100 text-admin-text rounded hover:bg-gray-200 transition-colors"
                          >
                            詳細
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Load More */}
        {nextToken && (
          <div className="mt-4 text-center">
            <button
              onClick={handleLoadMore}
              disabled={loading}
              className="px-6 py-2 border border-gray-300 text-admin-text rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              {loading ? '読み込み中...' : 'さらに読み込む'}
            </button>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedRecord && (
        <UploadDetailModal record={selectedRecord} onClose={() => setSelectedRecord(null)} />
      )}
    </div>
  );
};

const UploadDetailModal = ({
  record,
  onClose,
}: {
  record: UploadRecord;
  onClose: () => void;
}) => {
  const status = STATUS_LABELS[record.status] || {
    text: record.status,
    className: 'bg-gray-100 text-gray-800',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-bold text-admin-text">アップロード詳細</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            &times;
          </button>
        </div>
        <div className="p-4 space-y-3">
          <DetailRow label="ID" value={record.id} />
          <DetailRow label="種別" value={record.upload_type} />
          <DetailRow label="ファイル名" value={record.file_name} />
          <DetailRow label="S3 Key" value={record.s3_key} mono />
          <DetailRow label="担当者" value={record.upload_user} />
          <DetailRow
            label="ステータス"
            value={
              <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${status.className}`}>
                {status.text}
              </span>
            }
          />
          <DetailRow label="作成日時" value={new Date(record.created_at).toLocaleString('ja-JP')} />
          <DetailRow label="更新日時" value={new Date(record.updated_at).toLocaleString('ja-JP')} />

          {/* Success info */}
          {record.status === 'SUCCESS' && (
            <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
              <h3 className="font-semibold text-green-800 mb-2">処理結果</h3>
              <div className="text-sm text-green-700 space-y-1">
                <p>成功件数: {record.success_count ?? 0}</p>
              </div>
            </div>
          )}

          {/* Partial success info */}
          {record.status === 'PARTIAL_SUCCESS' && (
            <div className="mt-4 bg-orange-50 border border-orange-200 rounded-lg p-3">
              <h3 className="font-semibold text-orange-800 mb-2">処理結果（一部失敗）</h3>
              <div className="text-sm text-orange-700 space-y-1">
                <p>成功件数: {record.success_count ?? 0}</p>
                <p>失敗件数: {record.failed_count ?? 0}</p>
                {record.error_message && (
                  <p className="whitespace-pre-wrap break-all">{record.error_message}</p>
                )}
              </div>
            </div>
          )}

          {/* Error info */}
          {record.status === 'FAILED' && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3">
              <h3 className="font-semibold text-red-800 mb-2">エラー情報</h3>
              <div className="text-sm text-red-700 space-y-1">
                {record.error_message && (
                  <p className="whitespace-pre-wrap break-all">{record.error_message}</p>
                )}
                <p>失敗件数: {record.failed_count ?? 0}</p>
              </div>
            </div>
          )}
        </div>
        <div className="p-4 border-t text-right">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 text-admin-text rounded-lg hover:bg-gray-200 transition-colors"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

const DetailRow = ({
  label,
  value,
  mono,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) => (
  <div className="flex">
    <dt className="w-28 shrink-0 text-sm font-medium text-admin-textLight">{label}</dt>
    <dd className={`text-sm text-admin-text break-all ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
  </div>
);

export default UploadList;
